// EN mirror of /about/team — credentials of the 6 notarial attorneys
import { createFileRoute, Link } from "@tanstack/react-router";
import { Scale, GraduationCap, Languages, ArrowRight } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ATTORNEYS } from "@/data/attorneys";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Our Team — 6 Court-Licensed Notarial Attorneys | NYC Language Institute";
const DESC =
  "Meet the 6 notarial attorneys & sworn translators behind NYC Legal Bangkok. Get your timeline and checklist in minutes — chat now.";

export const TEAM = [
  { name: "Khun Sirichai P.", role: "Senior Notarial Attorney", langs: "TH · EN · JP", years: 22, specialty: "Corporate POA, real-estate, cross-border M&A" },
  { name: "Khun Achara T.", role: "Notarial Attorney · Court Interpreter", langs: "TH · EN · CN", years: 15, specialty: "Family law affidavits, marriage, adoption" },
  { name: "Khun Wirote S.", role: "Notarial Attorney", langs: "TH · EN", years: 18, specialty: "Apostille handling, MFA liaison" },
  { name: "Khun Pim K.", role: "Sworn Translator · Notarial Attorney", langs: "TH · EN · DE", years: 12, specialty: "German embassy submissions, education credentials" },
  { name: "Khun Boonsong R.", role: "Notarial Attorney", langs: "TH · EN", years: 25, specialty: "Power of attorney, business contracts" },
  { name: "Khun Nattaya L.", role: "Notarial Attorney · Translator", langs: "TH · EN · JP · KR", years: 10, specialty: "Japan/Korea visa documentation, hospital records" },
];

const URL = `${SITE}/en/about/team`;

export const Route = createFileRoute("/en/about/team/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/about/team", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/about/team", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/about/team", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/about/team", "en")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_US" },
      { property: "og:type", content: "profile" },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/about/team", "/en/about/team"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["AboutPage", "ProfilePage"],
          "@id": `${URL}#webpage`,
          url: URL,
          name: TITLE,
          description: DESC,
          inLanguage: "en",
          isPartOf: { "@id": `${SITE}#website` },
          mainEntity: { "@id": `${URL}#org` },
          about: ATTORNEYS.map((a) => ({ "@id": `${URL}#${a.license.replace("/", "-")}` })),
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".aeo-speakable", "h1", "h2"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["LegalService", "Organization"],
          "@id": `${URL}#org`,
          name: "NYC Legal & Notary Service Co., Ltd.",
          url: SITE,
          sameAs: [`${SITE}#org`],
          employee: ATTORNEYS.map((a) => ({
            "@type": "Person",
            "@id": `${URL}#${a.license.replace("/", "-")}`,
            mainEntityOfPage: { "@id": `${URL}#webpage` },
            name: a.nameEn,
            alternateName: a.nameTh,
            jobTitle: a.roleEn,
            worksFor: { "@id": `${SITE}#org` },
            image: `${SITE}${a.image}`,
            description: a.bioEn,
            knowsAbout: a.specialtiesEn,
            knowsLanguage: a.languages.map((l) => l.toLowerCase().slice(0, 2)),
            award: `${a.yearsExp}+ years legal practice in Thailand`,
            memberOf: {
              "@type": "Organization",
              name: "Lawyers Council of Thailand",
              url: "https://www.lawyerscouncil.or.th/2019/",
            },
            hasOccupation: {
              "@type": "Occupation",
              name: a.roleEn,
              occupationLocation: { "@type": "Country", name: "Thailand" },
              skills: a.specialtiesEn.join(", "),
            },
            hasCredential: {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "license",
              name: `Notary Public License No. ${a.license}`,
              recognizedBy: { "@type": "Organization", name: "Lawyers Council of Thailand" },
              dateCreated: a.licenseYear,
            },
            url: `${SITE}/en/about/team/${a.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "About", item: `${SITE}/en/about/team` },
            { "@type": "ListItem", position: 3, name: "Team", item: URL },
          ],
        }),
      },
    ],
  }),
  component: EnTeamPage,
});

function EnTeamPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="aeo-speakable mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="text-amber-300">6 licensed</span> notarial attorneys
          </h1>
          <p className="aeo-speakable mt-4 max-w-2xl text-white/80">
            Every notarisation we issue is signed in person by a court-licensed notarial attorney registered under the
            Lawyers Council of Thailand. Combined experience: 100+ years across Bangkok, Ladprao HQ since 2007.
          </p>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-6 text-2xl font-extrabold text-navy-deep">Licensed Notarial Attorneys</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {ATTORNEYS.map((a) => (
              <Link
                key={a.slug}
                to="/en/about/team/$slug"
                params={{ slug: a.slug }}
                className="group block rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-amber-400 hover:shadow-md"
              >
                <Scale className="h-6 w-6 text-amber-600" />
                <h3 className="mt-3 text-base font-bold text-navy-deep group-hover:text-amber-700">{a.nameEn}</h3>
                <p className="text-xs uppercase tracking-widest text-amber-700">{a.roleEn}</p>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><Languages className="h-3.5 w-3.5" />{a.languages.join(" · ")}</p>
                  <p className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5" />{a.yearsExp}+ yrs · License No. {a.license}</p>
                </div>
                <p className="mt-3 line-clamp-3 text-xs text-slate-500">{a.bioEn}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 group-hover:underline">
                  View profile <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>

          <h2 className="mt-12 mb-6 text-2xl font-extrabold text-navy-deep">Extended Team — Translators &amp; Interpreters</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((t) => (
              <article key={t.name} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <Scale className="h-6 w-6 text-amber-600" />
                <h3 className="mt-3 text-base font-bold text-navy-deep">{t.name}</h3>
                <p className="text-xs uppercase tracking-widest text-amber-700">{t.role}</p>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><Languages className="h-3.5 w-3.5" />{t.langs}</p>
                  <p className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5" />{t.years} yrs · {t.specialty}</p>
                </div>
              </article>
            ))}
          </div>
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
